import React, { ReactElement } from "react"
import styled from "styled-components"
import moment from "moment"
import { DataVisualizationGranularity } from "types/data-visualization.types"

// All date operations use UTC to ensure timezone-agnostic behavior

interface DateRange {
  startDate: Date
  endDate: Date
}

type PresetHandler = () => void

interface StyledDatePickerProps {
  value?: DateRange
  onChange: (value: DateRange) => void
  placeholder?: string
  label?: string
  required?: boolean
  width?: string
  height?: string
  disabled?: boolean
  updateGranularity: (granularity: DataVisualizationGranularity) => void
}

const DatePickerContainer = styled.div<{ width?: string }>`
  display: flex;
  flex-direction: column;
  width: ${props => props.width || "100%"};
`

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 6px;
  display: block;
`

const DateInputsContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`

const DateInput = styled.input<{ height?: string }>`
  background-color: white;
  border: 1px solid #e1e5e9;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  height: 24px;
  transition: all 0.2s ease;
  outline: none;
  font-size: 10px !important;
  line-height: 10px !important;
  padding: 0px 4px !important;
  width: 90px !important;
  font-family: Poppins;
  flex: 1;

  &:focus {
    border-color: #3498db;
    box-shadow: 0 2px 8px rgba(52, 152, 219, 0.15);
  }

  &:hover:not(:focus) {
    border-color: #c1c7cd;
  }

  &:disabled {
    background-color: #f8f9fa;
    cursor: not-allowed;
    opacity: 0.7;
  }
`

const Separator = styled.span`
  color: #666;
  font-size: 10px;
  font-weight: 500;
`

const QuickSelectContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 8px;
`

const PresetRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 2px;
  align-items: center;
`

const PresetLabel = styled.span`
  font-size: 10px;
  font-weight: 600;
  color: #666;
  margin-right: 8px;
  min-width: 60px;
  text-align: right;
`

const QuickSelectButton = styled.button`
  background-color: #f8f9fa;
  border: 1px solid #e1e5e9;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 10px !important;
  color: #666;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  min-width: fit-content;

  &:hover {
    background-color: #e9ecef;
    border-color: #c1c7cd;
  }

  &:active {
    background-color: #3498db;
    color: white;
    border-color: #3498db;
  }
`

export default function StyledDatePicker({
  value,
  onChange,
  label,
  required = false,
  width,
  height,
  disabled = false,
  updateGranularity,
}: StyledDatePickerProps): ReactElement {
  const formatDateForInput = (date: Date): string => {
    return moment.utc(date).format("YYYY-MM-DD")
  }

  const handleStartDateChange = (dateString: string): void => {
    const newStartDate = moment.utc(dateString).toDate()
    const endDate = value?.endDate || moment.utc().toDate()
    onChange({ startDate: newStartDate, endDate })
  }

  const handleEndDateChange = (dateString: string): void => {
    const newEndDate = moment.utc(dateString).toDate()
    const startDate = value?.startDate || moment.utc().toDate()
    onChange({ startDate, endDate: newEndDate })
  }

  const setLastWeek: PresetHandler = (): void => {
    const lastWeekStart = moment.utc().subtract(1, "week").startOf("isoWeek").toDate()
    const lastWeekEnd = moment.utc().subtract(1, "week").endOf("isoWeek").toDate()

    onChange({ startDate: lastWeekStart, endDate: lastWeekEnd })
    updateGranularity(DataVisualizationGranularity.WEEKLY)
  }

  const setLast2Weeks: PresetHandler = (): void => {
    const twoWeeksAgoStart = moment.utc().subtract(2, "weeks").startOf("isoWeek").toDate()
    const lastWeekEnd = moment.utc().subtract(1, "week").endOf("isoWeek").toDate()

    onChange({ startDate: twoWeeksAgoStart, endDate: lastWeekEnd })
    updateGranularity(DataVisualizationGranularity.WEEKLY)
  }

  const setLastMonth: PresetHandler = (): void => {
    const lastMonthStart = moment.utc().subtract(1, "month").startOf("month").toDate()
    const lastMonthEnd = moment.utc().subtract(1, "month").endOf("month").toDate()

    onChange({ startDate: lastMonthStart, endDate: lastMonthEnd })
    updateGranularity(DataVisualizationGranularity.MONTHLY)
  }

  const setLast3Months: PresetHandler = (): void => {
    const threeMonthsAgoStart = moment.utc().subtract(3, "months").startOf("month").toDate()
    const lastMonthEnd = moment.utc().subtract(1, "month").endOf("month").toDate()

    onChange({ startDate: threeMonthsAgoStart, endDate: lastMonthEnd })
    updateGranularity(DataVisualizationGranularity.MONTHLY)
  }

  // Ongoing periods (current periods)
  const setCurrentWeek: PresetHandler = (): void => {
    const currentWeekStart = moment.utc().startOf("isoWeek").toDate()
    const currentWeekEnd = moment.utc().endOf("isoWeek").toDate()

    onChange({ startDate: currentWeekStart, endDate: currentWeekEnd })
    updateGranularity(DataVisualizationGranularity.WEEKLY)
  }

  const setCurrentMonth: PresetHandler = (): void => {
    const currentMonthStart = moment.utc().startOf("month").toDate()
    const currentMonthEnd = moment.utc().endOf("month").toDate()

    onChange({ startDate: currentMonthStart, endDate: currentMonthEnd })
    updateGranularity(DataVisualizationGranularity.MONTHLY)
  }

  const setCurrent3Months: PresetHandler = (): void => {
    const threeMonthsPeriodStart = moment.utc().subtract(2, "months").startOf("month").toDate()
    const currentMonthEnd = moment.utc().endOf("month").toDate()

    onChange({ startDate: threeMonthsPeriodStart, endDate: currentMonthEnd })
    updateGranularity(DataVisualizationGranularity.MONTHLY)
  }

  return (
    <DatePickerContainer width={width}>
      {label && (
        <Label>
          {label}
          {required && <span style={{ color: "#e74c3c" }}>*</span>}
        </Label>
      )}
      <DateInputsContainer>
        <DateInput
          type="date"
          value={value?.startDate ? formatDateForInput(value.startDate) : ""}
          onChange={e => handleStartDateChange(e.target.value)}
          height={height}
          disabled={disabled}
          placeholder="Date de début"
        />
        <Separator>au</Separator>
        <DateInput
          type="date"
          value={value?.endDate ? formatDateForInput(value.endDate) : ""}
          onChange={e => handleEndDateChange(e.target.value)}
          height={height}
          disabled={disabled}
          placeholder="Date de fin"
        />
      </DateInputsContainer>

      <QuickSelectContainer>
        <PresetRow>
          <PresetLabel>En cours</PresetLabel>
          <QuickSelectButton type="button" onClick={setCurrentWeek}>
            S en cours
          </QuickSelectButton>
          <QuickSelectButton type="button" onClick={setCurrentMonth}>
            M en cours
          </QuickSelectButton>
          <QuickSelectButton type="button" onClick={setCurrent3Months}>
            3M
          </QuickSelectButton>
        </PresetRow>

        <PresetRow>
          <PresetLabel>Passé</PresetLabel>
          <QuickSelectButton type="button" onClick={setLastWeek}>
            S-1
          </QuickSelectButton>
          <QuickSelectButton type="button" onClick={setLast2Weeks}>
            S-1 + S-2
          </QuickSelectButton>
          <QuickSelectButton type="button" onClick={setLastMonth}>
            M-1
          </QuickSelectButton>
          <QuickSelectButton type="button" onClick={setLast3Months}>
            3 derniers mois
          </QuickSelectButton>
        </PresetRow>
      </QuickSelectContainer>
    </DatePickerContainer>
  )
}
